// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

export const fonts = {
  body: "-apple-system, 'ヒラギノ角ゴ Pro W3', 'Hiragino Kaku Gothic Pro', 'Helvetica Neue', 'Helvetica', 'Roboto', 'Noto', 'Arial', 'Lucida Grande', sans-serif;",
  monospace: 'Menlo, monospace',
  lp: '"fot-seurat-pron","ヒラギノ丸ゴ Pro W4","ヒラギノ丸ゴ Pro","Hiragino Maru Gothic Pro","ヒラギノ角ゴ Pro W3","Hiragino Kaku Gothic Pro","HG丸ｺﾞｼｯｸM-PRO","HGMaruGothicMPRO",sans-serif;'
};

export const radii = {
  large: '0.5rem',
  default: '0.25rem',
  small: '0.125rem',
  round: '50rem',
  circle: '50%'
};

export const zIndex = {
  navigation: 10,
  controlBar: 15,
  modal: 20,
  loading: 25,
  popOver: 30,
  notificationGroup: 40
};

enum BreakpointEnum {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl'
}

const breakpointValues = {
  [BreakpointEnum.xs]: '20rem', // 320px phone
  [BreakpointEnum.sm]: '35.5rem', // 568px tablet
  [BreakpointEnum.md]: '48rem', // 768px small laptop
  [BreakpointEnum.lg]: '64rem', // 1024px desktop
  [BreakpointEnum.xl]: '90rem' // 1440px large screen
};

const breakpointAliaseValues = {
  [0]: breakpointValues[BreakpointEnum.xs],
  [1]: breakpointValues[BreakpointEnum.sm],
  [2]: breakpointValues[BreakpointEnum.md],
  [3]: breakpointValues[BreakpointEnum.lg],
  [4]: breakpointValues[BreakpointEnum.xl]
};

const breakpoints = {
  ...breakpointValues,
  ...breakpointAliaseValues
};

const mediaQueries = {
  min: {
    xs: `@media screen and (min-width: ${breakpoints.xs})`,
    sm: `@media screen and (min-width: ${breakpoints.sm})`,
    md: `@media screen and (min-width: ${breakpoints.md})`,
    lg: `@media screen and (min-width: ${breakpoints.lg})`,
    xl: `@media screen and (min-width: ${breakpoints.xl})`
  },
  max: {
    xs: `@media screen and (max-width: ${breakpoints.xs})`,
    sm: `@media screen and (max-width: ${breakpoints.sm})`,
    md: `@media screen and (max-width: ${breakpoints.md})`,
    lg: `@media screen and (max-width: ${breakpoints.lg})`,
    xl: `@media screen and (max-width: ${breakpoints.xl})`
  }
};

const fontSizes = {
  baseFontSize: '16px',
  fontWeight: 'normal',

  h1: {
    fontSize: '2rem',
    fontWeight: 'bold',
    lineHeight: '1.3',
    mobile: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      lineHeight: '1.3'
    }
  },

  h2: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    lineHeight: '1.3',
    mobile: {
      fontSize: '1.125rem',
      fontWeight: 'bold',
      lineHeight: '1.3'
    }
  },

  h3: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    lineHeight: '1.3',
    mobile: {
      fontSize: '1rem',
      fontWeight: 'bold',
      lineHeight: '1.3'
    }
  },

  h4: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    lineHeight: '1.3',
    mobile: {
      fontSize: '1rem',
      fontWeight: 'bold',
      lineHeight: '1.3125'
    }
  },

  h5: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    lineHeight: '1.3',
    mobile: {
      fontSize: '1rem',
      fontWeight: 'bold',
      lineHeight: '1.3'
    }
  },

  h6: {
    fontSize: '1rem',
    fontWeight: 'bold',
    lineHeight: '1.3125',
    mobile: {
      fontSize: '1rem',
      fontWeight: 'bold',
      lineHeight: '1.5rem'
    }
  },

  text: {
    fontSize: '0.875rem',
    lineHeight: '1.43'
  },

  label: {
    fontSize: '0.75rem',
    fontWeight: 'bold',
    lineHeight: '1rem'
  },

  small: {
    fontSize: '0.75rem',
    lineHeight: '1.25'
  },

  exSmall: {
    fontSize: '0.625rem',
    lineHeight: '1.4'
  }
};

const buttonSizes = {
  sm: {
    padding: '0.25rem 1rem',
    iconSpace: '0.5rem',
    fontSize: fontSizes.small.fontSize,
    fontWeight: 'bold',
    lineHeight: '1rem'
  },
  md: {
    padding: '0.5rem 1rem',
    iconSpace: '0.5rem',
    fontSize: fontSizes.baseFontSize,
    fontWeight: 'normal',
    lineHeight: '1.25'
  },
  lg: {
    padding: '0.75rem 1.5rem',
    iconSpace: '0.75rem',
    fontSize: fontSizes.baseFontSize,
    fontWeight: 'bold',
    lineHeight: '1.3125'
  },
  xl: {
    padding: '1rem 2.5rem',
    iconSpace: '0.75rem',
    fontSize: fontSizes.h3.fontSize,
    fontWeight: 'bold',
    lineHeight: '1.3125'
  }
};

const radioSizes = {
  md: {
    boxSize: '1.25rem',
    borderWidth: '1px',
    dotSize: '0.75rem',
    labelFontSize: '0.875rem',
    labelSpacing: '0.5rem',
    radioSpacing: '1.5rem',
    radioVerticalSpacing: '0.75rem'
  },
  lg: {
    boxSize: '2rem',
    borderWidth: '2px',
    dotSize: '1.125rem',
    labelFontSize: '1.5rem',
    labelSpacing: '1rem',
    radioSpacing: '3rem',
    radioVerticalSpacing: '2rem'
  }
};

const checkboxSizes = {
  md: {
    width: '1.25rem',
    height: '1.25rem',
    borderWidth: '1px'
  },
  lg: {
    width: '2rem',
    height: '2rem',
    borderWidth: '2px'
  }
};

const modalSizes = {
  md: {
    width: '35rem',
    height: '94vh'
  },
  lg: {
    width: '50rem',
    height: '94vh'
  },
  fullscreen: {
    width: '98vw',
    height: '96vh'
  }
};

const iconButtonSizes = {
  sm: '1.5rem',
  md: '2.5rem',
  lg: '4rem',
  auto: 'auto'
};

const inputSizes = {
  sm: {
    fontSize: fontSizes.small.fontSize,
    lineHeight: fontSizes.small.lineHeight
  },
  md: {
    fontSize: fontSizes.text.fontSize,
    lineHeight: fontSizes.text.lineHeight
  }
};

export const defaultTheme = {
  breakpoints,
  mediaQueries,
  fonts,
  fontSizes,
  radii,
  zIndex,
  modalSizes,
  iconButtonSizes,
  buttonSizes,
  radioSizes,
  checkboxSizes,
  inputSizes
};

export default defaultTheme;
